/**
 * Filter option sources — the declarative seam behind
 * `docs/design-system/ui-list-pages.md` § Filter Option Sources.
 *
 * A list page's filter on a foreign key should offer the entities that actually
 * appear on ≥1 row of the list's base scope, not the tenant's whole entity
 * universe. Offering the universe makes the filter a guessing game: most choices
 * yield zero rows, and the user cannot tell "no matches" from "never linked here".
 *
 * A page DECLARES where its candidates come from — it does not hand-wire a
 * restricted picker:
 *
 *   • **Shape B (client-paged)**: the whole row set is already on the client, so
 *     `deriveFacetOptions(rows, …)` in a `useMemo` IS the source. No backend work.
 *   • **Shape A (server-paged)**: a `GET …/{entities}/filter-facets` endpoint
 *     returns `{ field: [{id, label, count}] }`. Reference implementation:
 *     `GET /api/v1/software-factory/work-items/filter-facets`
 *     (`modules/software_factory/services/work_item_filter_facets_service.py`).
 *
 * Either way the result is a {@link ListFilterFacet}, handed to a `ListFilterDef`
 * (`select`'s `facet`, or the `entity` variant) — `ListFilters` owns the rest.
 *
 * The two HONESTY INVARIANTS are implemented once, here and in the controls that
 * call {@link resolveCandidateRows}, rather than per page:
 *
 *   1. **The selected value is always in the candidate list**, even when the facet
 *      no longer contains it (last link removed; a shared URL opened by a user with
 *      narrower access). A filter that is active-but-blank misreads as inactive.
 *   2. **There is an escape hatch to the full universe** — a "Show all …" row that
 *      switches the control back to its normal full-search mode. The facet is a
 *      relevance default, not a wall: the user may be filtering *in order to
 *      confirm* an entity has no rows yet.
 */

/**
 * One candidate value, with how many rows of the base scope carry it.
 *
 * This is the DESIGN SYSTEM's vocabulary for any candidate source, not a hand-copy
 * of one module's response model. It also describes a Shape B facet derived on the
 * client (`deriveFacetOptions`), which no backend schema generates at all — so it
 * cannot be an alias of `SfWorkItemFilterFacetOption` without `shared/components/`
 * taking a dependency on `software_factory`'s generated API. Drift is still caught
 * where it matters: a page assigning a generated facet array to a `ListFilterFacet`
 * fails `tsc` the moment the two shapes disagree.
 */
// typed-surface-ok: the design system's own facet vocabulary — see above.
export interface FilterFacetOption {
  /** The referenced entity's id — the value the filter sends to the API. */
  id: string;
  /** Display label. Server-supplied on a Shape A facet, derived on a Shape B one. */
  label: string;
  /**
   * Rows in the base scope carrying this value. Omit where the source cannot
   * produce one cheaply — the control renders the row without a count rather
   * than inventing a zero.
   */
  count?: number | null;
}

/** A declared candidate source. */
export interface ListFilterFacet {
  /** The candidate set. Empty means "nothing is in use yet", which is a real answer. */
  options: FilterFacetOption[];
  /**
   * The facet query is still in flight.
   *
   * While true the control stays UNRESTRICTED (it offers the full universe) rather
   * than briefly offering nothing: a picker that flashes an empty list on every
   * mount reads as broken, and "no candidates" and "not loaded yet" are different
   * answers that must not look alike.
   */
  isLoading?: boolean;
  /**
   * The source could not carry every value in use.
   *
   * A Shape A facet is bounded server-side ({@link SERVER_FACET_OPTION_LIMIT}) —
   * nothing about the columns these facets sit on is low-cardinality by
   * construction, and the indexes that make the `GROUP BY` fast do not make the
   * response small. A truncated dropdown that looks COMPLETE would be worse than
   * an uncapped one, so the ceiling is not hidden: the controls say so on the
   * escape-hatch row (invariant 2), which is already the way out to the full
   * universe. Set it with {@link serverFacet} rather than by hand.
   */
  truncated?: boolean;
}

/**
 * Most options a Shape A `…/filter-facets` endpoint will return per field.
 *
 * The mirror of `FACET_OPTION_LIMIT` in `shared/repositories/filter_facets.py`,
 * which is where the number and its reasoning live. The two are pinned to each
 * other by `shared/repositories/__tests__/test_filter_facet_limit_parity.py`, so
 * changing one alone fails the build.
 */
export const SERVER_FACET_OPTION_LIMIT = 500;

/**
 * Build a {@link ListFilterFacet} from a Shape A `…/filter-facets` response.
 *
 * The wire payload is `{field: [{id, label, count}]}` and carries no truncation
 * flag of its own, so the client derives one by comparing the option count
 * against the ceiling it knows the server applies. That is exact except at
 * exactly {@link SERVER_FACET_OPTION_LIMIT} values in use, where it says "there
 * may be more" about a set that happens to be complete — an error in the honest
 * direction, and one that only points at the escape hatch the control already
 * renders.
 *
 * A Shape B facet derived on the client (`deriveFacetOptions`) is never
 * truncated and does not go through here.
 */
export function serverFacet(
  options: FilterFacetOption[] | undefined,
  isLoading: boolean,
): ListFilterFacet {
  const rows = options ?? [];
  return {
    options: rows,
    isLoading,
    truncated: rows.length >= SERVER_FACET_OPTION_LIMIT,
  };
}

/**
 * The props a picker accepts to become facet-sourced.
 *
 * Spread onto the picker inside a `ListFilterDef`'s `render` — `ListFilters` builds
 * the object, including the "Show all …" toggle state, so the page declares the
 * SOURCE and nothing else. Pickers implementing this include `EntityPickerBase`
 * (and its wrappers), `UserPicker`, `AccountPicker`, `CountryPicker`,
 * `MultiOfficePicker`, `TenantPicker`, `PlacePicker`, `EmployeePicker`, and
 * `ProductReferencePicker`.
 */
export interface EntityFilterCandidates {
  /**
   * Offer exactly these instead of the picker's own full search. `undefined` is
   * the picker's normal unrestricted mode — which is also what the "Show all …"
   * row switches it back to.
   */
  candidates?: FilterFacetOption[];
  /** Leave facet mode. Omit to render no escape-hatch row. */
  onShowAllCandidates?: () => void;
  /** Label for the escape-hatch row, e.g. "Show all {{organizations}}…". */
  showAllCandidatesLabel?: string;
}

/**
 * Derive a facet from rows already on the client — the Shape B source.
 *
 * Counts fall out of the same pass, so a client-derived facet is never the
 * count-less kind. Rows carrying no value for the field are skipped (a "no value"
 * option is a separate question, and `MultiSelectFilter` owns it).
 *
 * Call it inside a `useMemo` keyed on the row array: it allocates a new array, and
 * a fresh identity every render would defeat the memo of whatever consumes it.
 */
export function deriveFacetOptions<T>(
  rows: readonly T[],
  getId: (row: T) => string | null | undefined,
  getLabel: (row: T) => string | null | undefined,
): FilterFacetOption[] {
  const byId = new Map<string, FilterFacetOption>();
  for (const row of rows) {
    const id = getId(row);
    if (!id) continue;
    const existing = byId.get(id);
    if (existing) {
      existing.count = (existing.count ?? 0) + 1;
      continue;
    }
    byId.set(id, { id, label: getLabel(row) || id, count: 1 });
  }
  return sortFacetOptions([...byId.values()]);
}

/**
 * Most-used first, ties broken alphabetically.
 *
 * That order is what makes a facet worth reading over an alphabetical universe —
 * the values a user is most likely to want are the ones the list is mostly made
 * of. Mirrors the server-side ordering in `WorkItemFilterFacetsService._options`,
 * so a Shape A and a Shape B facet read the same way.
 *
 * Copies before sorting. `Array.prototype.sort` mutates, and this is exported for
 * any facet source to use — the obvious next caller hands it an array it does not
 * own (a React Query cache entry), where an in-place sort would silently reorder
 * the cache.
 */
export function sortFacetOptions(
  options: readonly FilterFacetOption[],
): FilterFacetOption[] {
  return [...options].sort(
    (a, b) => (b.count ?? 0) - (a.count ?? 0) || a.label.localeCompare(b.label),
  );
}

export interface ResolveCandidateRowsArgs {
  /** The candidate set. */
  candidates: FilterFacetOption[];
  /** The control's current search text. Empty means no search. */
  search?: string;
  /** The currently selected id, if any. */
  value?: string | null;
  /**
   * Label for the selected id when it is NOT in the facet — from whatever the
   * picker already knows (its own by-id fetch, a cached label). Falls back to the
   * raw id, which is ugly but honest: a blank row would read as no selection.
   */
  selectedLabel?: string | null;
}

/**
 * The rows a facet-sourced control renders: the candidate set with the selected
 * value pinned into it (invariant 1), then filtered by the search text.
 *
 * Search is applied CLIENT-side and that is deliberate — a facet is bounded by
 * construction (it is the set in use on one list), so a round trip per keystroke
 * would buy nothing. It also means the search filters the pinned row like any
 * other: the invariant is that the selected value is in the CANDIDATE list, not
 * that it survives a query it does not match.
 */
export function resolveCandidateRows({
  candidates,
  search,
  value,
  selectedLabel,
}: ResolveCandidateRowsArgs): FilterFacetOption[] {
  let rows = candidates;
  if (value && !candidates.some((c) => c.id === value)) {
    rows = [{ id: value, label: selectedLabel || value }, ...candidates];
  }
  const query = search?.trim().toLowerCase();
  if (!query) return rows;
  return rows.filter((row) => row.label.toLowerCase().includes(query));
}
